import type {
  ControllerKind,
  DynamicControllerBuilderMisc,
  SubjectEntity,
} from "../Processing/types";
import { controllerAthlete, controllerSingleDate } from "../Processing/controllerBindings";
import { resolveDateRange } from "./resolveDateRange";
import { resolveSubjectNames } from "./subjectNames";

export type SubjectResolver = (subjectType: string) => SubjectEntity[];

/**
 * The one-line summary a controller's picker state produces, per kind. `controls` decides
 * where a single-player summary reads its athlete from (the legacy picker, or the first
 * athlete of the subject picker) and where a single-date summary reads its day from (the
 * legacy picker, or the end of the date range). Callers without the controls (the PDF
 * snapshot, which only renders kinds the controller itself carries) may omit it.
 *
 * Component-free on purpose: the slicer PDF snapshot and the headless renderer read it
 * without the antd section header that also uses it.
 */
export function controllerSummary(
  kind: ControllerKind,
  misc: DynamicControllerBuilderMisc,
  getSubjectEntities?: SubjectResolver,
  controls?: ControllerKind[],
): string {
  if (kind === "date") return resolveDateRange(misc);

  if (kind === "single_date") {
    const day = controllerSingleDate(controls ?? ["single_date"], misc);
    if (day.dateMode === "absolute") return day.fixedDate || "no date set";
    return day.dateOffset === 0 ? "today" : `${day.dateOffset}d`;
  }

  if (kind === "single_player") {
    const athlete = controllerAthlete(controls ?? ["single_player"], misc);
    if (!athlete) return "none";
    if (!getSubjectEntities) return athlete;
    return (
      getSubjectEntities("player").find((p) => `player-${p.id}` === athlete)?.name
      ?? athlete
    );
  }

  const subjects = misc.selectedSubjects ?? [];
  const drills = misc.selectedDrills ?? [];
  if (subjects.length === 0 && drills.length === 0) return "Full Team";
  const names = getSubjectEntities
    ? resolveSubjectNames(subjects, drills, getSubjectEntities)
    : [...subjects, ...drills];
  return names.length <= 2 ? names.join(", ") : `${names.length} selected`;
}

/**
 * The roomier summary for a tile with a second text line. Only the subject kind gains
 * detail: it names the selected athletes and drills, with an ellipsis after the first
 * three, instead of "N selected". Every other kind already reads well in one line.
 */
export function controllerSummaryDetailed(
  kind: ControllerKind,
  misc: DynamicControllerBuilderMisc,
  getSubjectEntities?: SubjectResolver,
  controls?: ControllerKind[],
): string {
  if (kind !== "subject") return controllerSummary(kind, misc, getSubjectEntities, controls);
  const subjects = misc.selectedSubjects ?? [];
  const drills = misc.selectedDrills ?? [];
  if (subjects.length === 0 && drills.length === 0) return "Full Team";
  const names = getSubjectEntities
    ? resolveSubjectNames(subjects, drills, getSubjectEntities)
    : [...subjects, ...drills];
  return names.length <= 3 ? names.join(", ") : `${names.slice(0, 3).join(", ")}, …`;
}
